import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { Integration, IntegrationWrite } from '../../../shared/models/integration.model';
import { IntegrationService } from '../services/integration.service';
import { IntegrationMetrics } from '../../../shared/models/integration-metrics.model';
import { LoadingService, OVERLAY_POSITION } from '../../../core/ui/loading.service';
import { TableQueryParams } from '../../../shared/models/table-query-params.model';
import { IntegrationVendorDashboard } from '../../../shared/models/integration-vendor-dashboard.model';
import { IntegrationFtpSettings, IntegrationFtpSettingsWrite } from '../../../shared/models/integration-ftp-settings';

@Injectable({ providedIn: 'root' })
export class IntegrationStore {
  private loadingService = inject(LoadingService);

  // Subjects für den Zustand
  private integrationsSubject = new BehaviorSubject<Integration[]>([]);
  private totalRecordSubject = new BehaviorSubject<number>(0);
  private metricSubject = new BehaviorSubject<IntegrationMetrics[]>([]);

  // Öffentliche Observables
  integrations$: Observable<Integration[]> = this.integrationsSubject.asObservable();
  totalRecords$: Observable<number> = this.totalRecordSubject.asObservable();
  metrics$: Observable<IntegrationMetrics[]> = this.metricSubject.asObservable();

  constructor(private integrationService: IntegrationService) { }

  /**
   * Lädt eine Integration mit der angegebenen ID **vom Server**.
   */
  loadIntegrationById(integrationId: number): Observable<Integration> {
    this.loadingService.show();

    return this.integrationService.fetchIntegrationById(integrationId, false).pipe(
      catchError(err => {
        throw err;
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  /**
   * Gibt eine Integration aus dem lokalen Store zurück (falls vorhanden).
   */
  getIntegrationById(integrationId: number): Integration | null {
    const integrations = this.integrationsSubject.getValue();
    return integrations.find(i => i.id === integrationId) ?? null;
  }

  loadFtpWhitelist(): Observable<string[]> {
    this.loadingService.show();

    return this.integrationService.fetchFtpWhitelist().pipe(
      catchError(err => {
        throw err;
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  /**
 * Lädt alle Anbindungen zur Lieferanten-ID.
 */
  loadIntegrationsByVendorId(vendorId: number): void {
    this.loadingService.show();

    this.integrationService.fetchIntegrationsByVendorId(vendorId).pipe(
      finalize(() => this.loadingService.hide())
    ).subscribe(integrations => {
      this.integrationsSubject.next(integrations);
    });;
  }

  loadIntegrationFtpSettingsByIntegrationId(integrationId: number): Observable<IntegrationFtpSettings> {
    this.loadingService.show();
    return this.integrationService.fetchIntegrationFtpSettingsByIntegrationId(integrationId).pipe(
      catchError(err => {
        throw err;
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  loadPagedIntegrations(params: TableQueryParams): Observable<{ items: IntegrationVendorDashboard[]; totalCount: number }> {
    this.loadingService.show(OVERLAY_POSITION.COMPONENT)
    return this.integrationService.fetchPagedIntegrations(params).pipe(
      catchError(err => {
        throw err;
      }),
      finalize(() => this.loadingService.hide(OVERLAY_POSITION.COMPONENT))
    );
  }

  /**
   * Erstellt eine neue Anbindung und fügt ihn der Liste hinzu.
   */
  createIntegration(integration: IntegrationWrite): Observable<Integration> {
    this.loadingService.show();

    return this.integrationService.createIntegration(integration).pipe(
      tap(createdIntegration => {
        // Aktualisiere den Store, wenn die Anbindung erfolgreich erstellt wurde
        const currentIntegrations = this.integrationsSubject.getValue();
        this.integrationsSubject.next([...currentIntegrations, createdIntegration]);
      }),
      catchError(err => {
        throw err;
      }),
      finalize(() => {
        // Setze den Ladezustand zurück
        this.loadingService.hide();
      })
    );
  }

  /**
   * Aktualisiert eine bestehende Anbindung und ersetzt ihn in der Liste.
   */
  updateIntegration(integrationId: number, integration: IntegrationWrite): Observable<Integration> {
    this.loadingService.show();

    return this.integrationService.updateIntegration(integrationId, integration).pipe(
      tap(updatedIntegration => {
        // Aktualisiere den Store mit dem geupdateten Vendor
        const current = this.integrationsSubject.getValue();
        const updatedIntegrations = current.map(v => ((v.id === updatedIntegration.id) ? updatedIntegration : v));
        this.integrationsSubject.next(updatedIntegrations);
      }),
      catchError(err => {
        throw err;
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  /**
 * Aktualisiert die FTP-Einstellungen einer bestehenden Anbindung.
 */
  updateIntgrationFtpSettings(integrationId: number, integrationFtpSettings: IntegrationFtpSettingsWrite): Observable<boolean> {
    this.loadingService.show();

    return this.integrationService.updateIntegrationFtpSettings(integrationId, integrationFtpSettings).pipe(
      catchError(err => {
        throw err;
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  /**
   * Löscht einen Lieferanten anhand seiner ID.
   */
  deleteIntegration(integrationId: number): void {
    this.loadingService.show();

    this.integrationService.deleteIntegration(integrationId).pipe(
      catchError(err => {
        throw err;
      }),
      finalize(() => this.loadingService.hide())
    ).subscribe(() => {
      const currentIntegrations = this.integrationsSubject.getValue();
      this.integrationsSubject.next(currentIntegrations.filter(v => v.id !== integrationId));
    });
  }

  isCostCenterRelationDeletable(integrationId: number, costCenterId: number): Observable<boolean> {
    return this.integrationService.isCostCenterRelationDeletable(integrationId, costCenterId).pipe(
      catchError(err => {
        throw err;
      })
    );
  }

  loadMetrics(clientId?: number | null): void {
    this.loadingService.show();
    this.integrationService.fetchMetrics(clientId).pipe(
      catchError(err => {
        throw err;
      }),
      finalize(() => this.loadingService.hide())
    ).subscribe(response => {
      this.metricSubject.next(response);
    });
  }
}